"use client";

import Link from 'next/link';
import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HeroButton } from '@/components/HomePageClientParts';
import { Check, ArrowRight, Palette, Type, Layout, Globe, Sliders, Layers, Monitor, Eye, Brush, Code, CornerRightDown, ArrowDown, ArrowUp, Cpu, Play, Smartphone, Tablet } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { CyclingTypewriterEffect } from '@/components/ui/typewriter-effect';

export default function CustomizationPage() {
  const params = useParams();
  const locale = params?.locale as string || 'sv';
  const t = useTranslations('customization');

  return (
    <>
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-white dark:bg-gray-950">
        <div className="container px-4 md:px-6 max-w-7xl mx-auto">
          <div className="grid gap-12 lg:grid-cols-2 items-center">
            <div className="flex flex-col justify-center space-y-8">
              {/* Feature Badge */}
              <div className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-blue-500/10 text-blue-700 dark:text-blue-300 w-fit">
                <span className="relative flex h-2 w-2 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-600 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600"></span>
                </span>
                {t('hero.badge')}
              </div>
              
              {/* Hero heading */}
              <div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-gray-900 dark:text-white">
                  {t('hero.title').split(t('hero.titleHighlight'))[0]}
                  <span className="text-blue-600">{t('hero.titleHighlight')}</span>
                  {t('hero.title').split(t('hero.titleHighlight'))[1]}
                </h1>
                <div className="mt-4">
                  <CyclingTypewriterEffect
                    words={[
                      { text: t('hero.typewriter.visualControl'), className: "text-gray-600 dark:text-gray-300" },
                      { text: t('hero.typewriter.brandMatching'), className: "text-gray-600 dark:text-gray-300" },
                      { text: t('hero.typewriter.professionalTemplates'), className: "text-gray-600 dark:text-gray-300" },
                      { text: t('hero.typewriter.unlimitedCustomization'), className: "text-gray-600 dark:text-gray-300" }
                    ]}
                    className="text-xs md:text-sm font-normal min-h-[1.5rem]"
                    cursorClassName="h-4 bg-blue-500/80"
                  />
                </div>
              </div>
              
              {/* Brief description */}
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {t('hero.description')}
              </p>
              
              {/* CTA buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <HeroButton 
                  variant="default"
                  size="lg"
                  signedOutText={t('hero.cta')}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium shadow transition-colors" 
                />
              </div>
            </div>
            
            {/* Hero illustration */}
            <div className="relative hidden lg:block">
              <div className="absolute -top-12 -left-12 w-40 h-40 bg-blue-100 dark:bg-blue-900/20 rounded-lg blur-3xl opacity-60"></div>
              <div className="absolute -bottom-12 -right-12 w-40 h-40 bg-purple-100 dark:bg-purple-900/20 rounded-lg blur-3xl opacity-60"></div>
              
              <div className="relative rounded-lg border border-gray-200 dark:border-gray-800 shadow-lg bg-white dark:bg-gray-900 z-10 overflow-hidden">
                <div className="bg-gray-100 dark:bg-gray-800 p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">{t('heroIllustration.windowTitle')}</div>
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                </div>
                
                <div className="p-6">
                  {/* Template preview cards */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="relative rounded-lg border-2 border-blue-500 overflow-hidden">
                      <div className="absolute top-2 right-2 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      <div className="h-20 bg-gray-50 dark:bg-gray-800 p-2">
                        <div className="h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full mb-2"></div>
                        <div className="h-2 w-3/4 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                      </div>
                    </div>
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <div className="h-20 bg-gray-50 dark:bg-gray-800 p-2">
                        <div className="h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full mb-2"></div>
                        <div className="h-2 w-3/4 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                      </div>
                    </div>
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <div className="h-20 bg-gray-50 dark:bg-gray-800 p-2">
                        <div className="h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full mb-2"></div>
                        <div className="h-2 w-3/4 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Style controls */}
                  <div className="space-y-6">
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('heroIllustration.bannerColors')}</div>
                      <div className="flex space-x-3">
                        <div className="w-8 h-8 rounded-full bg-blue-500 ring-2 ring-offset-2 ring-blue-500"></div>
                        <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                        <div className="w-8 h-8 rounded-full bg-gray-900 dark:bg-gray-300"></div>
                        <div className="w-8 h-8 rounded-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700"></div>
                        <div className="w-8 h-8 rounded-full bg-green-500"></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('heroIllustration.layout')}</div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="h-12 rounded border-2 border-blue-500 flex items-center justify-center bg-white dark:bg-gray-900">
                          <div className="w-8 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        </div>
                        <div className="h-12 rounded border border-gray-200 dark:border-gray-700 flex items-center justify-center bg-white dark:bg-gray-900">
                          <div className="w-8 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        </div>
                        <div className="h-12 rounded border border-gray-200 dark:border-gray-700 flex items-center justify-center bg-white dark:bg-gray-900">
                          <div className="w-8 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('heroIllustration.position')}</div>
                        <div className="h-10 w-full bg-gray-50 dark:bg-gray-800 rounded flex items-center px-3">
                          <span className="text-xs text-gray-500 dark:text-gray-400">{t('heroIllustration.positionBottom')}</span>
                          <CornerRightDown className="w-3 h-3 text-gray-400 ml-auto" />
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('heroIllustration.animation')}</div>
                        <div className="h-10 w-full bg-gray-50 dark:bg-gray-800 rounded flex items-center px-3">
                          <span className="text-xs text-gray-500 dark:text-gray-400">{t('heroIllustration.animationSlideUp')}</span>
                          <CornerRightDown className="w-3 h-3 text-gray-400 ml-auto" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Templates Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="container px-4 md:px-6 max-w-7xl mx-auto">
          <div className="text-center max-w-[800px] mx-auto mb-16">
            <div className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-blue-500/10 text-blue-700 dark:text-blue-300 w-fit mx-auto mb-6">
              <Layers className="h-3.5 w-3.5 mr-1.5" />
              {t('templates.badge')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">{t('templates.title')}</h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              {t('templates.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Template 1 - Modern */}
            <div className="flex flex-col">
              <div className="relative rounded-t-lg overflow-hidden border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
                <div className="absolute top-4 left-4 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium px-2.5 py-0.5 rounded">
                  {t('templates.mostPopular')}
                </div>
                <div className="pt-12 px-6 pb-6">
                  <div className="h-4 w-full bg-gray-100 dark:bg-gray-800 rounded mb-3"></div>
                  <div className="h-4 w-5/6 bg-gray-100 dark:bg-gray-800 rounded mb-3"></div>
                  <div className="h-4 w-4/6 bg-gray-100 dark:bg-gray-800 rounded mb-6"></div>
                  <div className="flex justify-end space-x-3">
                    <div className="h-8 w-24 bg-gray-100 dark:bg-gray-800 rounded"></div>
                    <div className="h-8 w-24 bg-blue-600 rounded"></div>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-900 border-x border-b border-gray-200 dark:border-gray-800 rounded-b-lg p-6">
                <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">{t('templates.modern.title')}</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">{t('templates.modern.description')}</p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                    {t('templates.modern.tags.elegant')}
                  </Badge>
                  <Badge variant="outline" className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                    {t('templates.modern.tags.professional')}
                  </Badge>
                  <Badge variant="outline" className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                    {t('templates.modern.tags.bottomPosition')}
                  </Badge>
                </div>
              </div>
            </div>
            
            {/* Template 2 - Compact */}
            <div className="flex flex-col">
              <div className="rounded-t-lg overflow-hidden border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
                <div className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex-shrink-0"></div>
                    <div className="flex-1">
                      <div className="h-4 w-full bg-gray-100 dark:bg-gray-800 rounded mb-3"></div>
                      <div className="h-4 w-5/6 bg-gray-100 dark:bg-gray-800 rounded mb-3"></div>
                      <div className="h-4 w-4/6 bg-gray-100 dark:bg-gray-800 rounded mb-4"></div>
                      <div className="flex space-x-3">
                        <div className="h-8 w-20 bg-gray-100 dark:bg-gray-800 rounded"></div>
                        <div className="h-8 w-20 bg-blue-600 rounded"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-900 border-x border-b border-gray-200 dark:border-gray-800 rounded-b-lg p-6">
                <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">{t('templates.compact.title')}</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">{t('templates.compact.description')}</p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                    {t('templates.compact.tags.discrete')}
                  </Badge>
                  <Badge variant="outline" className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                    {t('templates.compact.tags.cornerPosition')}
                  </Badge>
                  <Badge variant="outline" className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                    {t('templates.compact.tags.minimal')}
                  </Badge>
                </div>
              </div>
            </div>
            
            {/* Template 3 - Advanced */}
            <div className="flex flex-col">
              <div className="rounded-t-lg overflow-hidden border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
                <div className="p-6">
                  <div className="h-4 w-5/6 bg-gray-100 dark:bg-gray-800 rounded mb-4"></div>
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center">
                      <div className="w-5 h-5 bg-blue-600 rounded-sm mr-3"></div>
                      <div className="h-4 w-1/3 bg-gray-100 dark:bg-gray-800 rounded"></div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-5 h-5 bg-gray-100 dark:bg-gray-800 rounded-sm mr-3"></div>
                      <div className="h-4 w-2/5 bg-gray-100 dark:bg-gray-800 rounded"></div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-5 h-5 bg-gray-100 dark:bg-gray-800 rounded-sm mr-3"></div>
                      <div className="h-4 w-1/4 bg-gray-100 dark:bg-gray-800 rounded"></div>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="h-8 w-32 bg-blue-600 rounded"></div>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-900 border-x border-b border-gray-200 dark:border-gray-800 rounded-b-lg p-6">
                <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">{t('templates.advanced.title')}</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">{t('templates.advanced.description')}</p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                    {t('templates.advanced.tags.detailed')}
                  </Badge>
                  <Badge variant="outline" className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                    {t('templates.advanced.tags.transparent')}
                  </Badge>
                  <Badge variant="outline" className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                    {t('templates.advanced.tags.granularControl')}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customization Options Section */}
      <section className="py-20 bg-white dark:bg-gray-950">
        <div className="container px-4 md:px-6 max-w-7xl mx-auto">
          <div className="text-center max-w-[800px] mx-auto mb-16">
            <div className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-purple-500/10 text-purple-700 dark:text-purple-300 w-fit mx-auto mb-6">
              <Palette className="h-3.5 w-3.5 mr-1.5" />
              {t('customization.badge')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">{t('customization.title')}</h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              {t('customization.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Visual Controls */}
            <Card className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
              <CardHeader className="pb-4">
                <div className="mb-4 p-3 rounded-lg bg-blue-500/10 inline-block">
                  <Palette className="h-6 w-6 text-blue-600" />
                </div>
                <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">{t('customization.visual.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30">
                      <Check className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('customization.visual.brandColors.title')}</h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {t('customization.visual.brandColors.description')}
                    </p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30">
                      <Check className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('customization.visual.typography.title')}</h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {t('customization.visual.typography.description')}
                    </p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30">
                      <Check className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('customization.visual.cornerRadius.title')}</h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {t('customization.visual.cornerRadius.description')}
                    </p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30">
                      <Check className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('customization.visual.bordersAndShadows.title')}</h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {t('customization.visual.bordersAndShadows.description')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Content Controls */}
            <Card className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
              <CardHeader className="pb-4">
                <div className="mb-4 p-3 rounded-lg bg-purple-500/10 inline-block">
                  <Type className="h-6 w-6 text-purple-600" />
                </div>
                <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">{t('customization.content.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30">
                      <Check className="h-4 w-4 text-purple-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('customization.content.customizableText.title')}</h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {t('customization.content.customizableText.description')}
                    </p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30">
                      <Check className="h-4 w-4 text-purple-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('customization.content.multiLanguage.title')}</h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {t('customization.content.multiLanguage.description')}
                    </p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30">
                      <Check className="h-4 w-4 text-purple-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('customization.content.buttonCustomization.title')}</h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {t('customization.content.buttonCustomization.description')}
                    </p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30">
                      <Check className="h-4 w-4 text-purple-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('customization.content.cookieDescriptions.title')}</h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {t('customization.content.cookieDescriptions.description')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Behavior Controls */}
            <Card className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
              <CardHeader className="pb-4">
                <div className="mb-4 p-3 rounded-lg bg-amber-500/10 inline-block">
                  <Sliders className="h-6 w-6 text-amber-600" />
                </div>
                <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">{t('customization.behavior.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30">
                      <Check className="h-4 w-4 text-amber-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('customization.behavior.positionAndLayout.title')}</h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {t('customization.behavior.positionAndLayout.description')}
                    </p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30">
                      <Check className="h-4 w-4 text-amber-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('customization.behavior.animationEffects.title')}</h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {t('customization.behavior.animationEffects.description')}
                    </p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30">
                      <Check className="h-4 w-4 text-amber-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('customization.behavior.displayTriggers.title')}</h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {t('customization.behavior.displayTriggers.description')}
                    </p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30">
                      <Check className="h-4 w-4 text-amber-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('customization.behavior.mobileOptimization.title')}</h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {t('customization.behavior.mobileOptimization.description')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Live Preview Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="container px-4 md:px-6 max-w-7xl mx-auto">
          <div className="grid gap-12 lg:grid-cols-2 items-center">
            <div>
              <div className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-green-500/10 text-green-700 dark:text-green-300 w-fit mb-6">
                <Eye className="h-3.5 w-3.5 mr-1.5" />
                {t('livePreview.badge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900 dark:text-white">{t('livePreview.title')}</h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
                {t('livePreview.subtitle')}
              </p>
              
              <div className="space-y-6">
                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30">
                      <Monitor className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('livePreview.devicePreview.title')}</h3>
                    <p className="mt-1 text-gray-600 dark:text-gray-400">
                      {t('livePreview.devicePreview.description')}
                    </p>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30">
                      <Brush className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('livePreview.styleEditor.title')}</h3>
                    <p className="mt-1 text-gray-600 dark:text-gray-400">
                      {t('livePreview.styleEditor.description')}
                    </p>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30">
                      <Layout className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('livePreview.layoutManager.title')}</h3>
                    <p className="mt-1 text-gray-600 dark:text-gray-400">
                      {t('livePreview.layoutManager.description')}
                    </p>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30">
                      <Code className="h-4 w-4 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('livePreview.codeExport.title')}</h3>
                    <p className="mt-1 text-gray-600 dark:text-gray-400">
                      {t('livePreview.codeExport.description')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="lg:ml-auto">
              <div className="relative rounded-lg border border-gray-200 dark:border-gray-800 p-1 shadow-lg bg-white dark:bg-gray-900 overflow-hidden">
                <div className="bg-gray-100 dark:bg-gray-800 rounded-t-lg p-2 flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 ml-2">{t('livePreview.windowTitle')}</div>
                </div>
                
                <div className="p-4">
                  <div className="relative bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden" style={{ height: '400px' }}>
                    {/* Simulated website content */}
                    <div className="p-4">
                      <div className="h-6 w-1/3 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>
                      <div className="h-4 w-full bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                      <div className="h-4 w-full bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                      <div className="h-4 w-3/4 bg-gray-300 dark:bg-gray-600 rounded mb-6"></div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="h-20 bg-gray-300 dark:bg-gray-600 rounded"></div>
                        <div className="h-20 bg-gray-300 dark:bg-gray-600 rounded"></div>
                      </div>
                      
                      <div className="h-4 w-full bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                      <div className="h-4 w-full bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                      <div className="h-4 w-2/3 bg-gray-300 dark:bg-gray-600 rounded"></div>
                    </div>
                    
                    {/* Cookie banner preview */}
                    <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="h-4 w-full bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
                          <div className="h-4 w-5/6 bg-gray-300 dark:bg-gray-700 rounded"></div>
                        </div>
                        <div className="flex shrink-0 gap-3">
                          <div className="h-8 w-28 bg-gray-300 dark:bg-gray-700 rounded"></div>
                          <div className="h-8 w-28 bg-blue-600 rounded"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('livePreview.previewMode')}</div>
                        <div className="flex space-x-2">
                          <button className="p-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                            <Monitor className="h-4 w-4" />
                          </button>
                          <button className="p-1.5 bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded">
                            <Smartphone className="h-4 w-4" />
                          </button>
                          <button className="p-1.5 bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded">
                            <Tablet className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('livePreview.bannerPosition')}</div>
                        <div className="flex space-x-2">
                          <button className="p-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                            <ArrowDown className="h-4 w-4" />
                          </button>
                          <button className="p-1.5 bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded">
                            <ArrowUp className="h-4 w-4" />
                          </button>
                          <button className="p-1.5 bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded">
                            <CornerRightDown className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

          {/* Enterprise Section */}
      <section className="py-20 bg-white dark:bg-gray-950">
        <div className="container px-4 md:px-6 max-w-7xl mx-auto">
          <div className="rounded-xl overflow-hidden shadow-lg">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 md:p-12">
              <div className="grid gap-8 lg:grid-cols-2 items-center">
                <div>
                  <Badge className="bg-white/20 text-white hover:bg-white/30 border-transparent mb-4">{t('enterprise.badge')}</Badge>
                  <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">{t('enterprise.title')}</h2>
                  <p className="text-xl mb-6 text-white/90">{t('enterprise.subtitle')}</p>
                  
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-white/20">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-white">{t('enterprise.features.dedicatedDesigner')}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-white/20">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-white">{t('enterprise.features.customIntegration')}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-white/20">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-white">{t('enterprise.features.specializedFunctionality')}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8">
                    <Button className="bg-white text-blue-600 hover:bg-white/90">
                      {t('enterprise.cta')}
                    </Button>
                  </div>
                </div>
                
                <div className="hidden lg:block bg-white/10 rounded-lg p-6">
                  <div className="space-y-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <Code className="h-10 w-10 text-white/80" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold text-white">{t('enterprise.services.customDevelopment.title')}</h3>
                        <p className="text-white/80">{t('enterprise.services.customDevelopment.description')}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <Brush className="h-10 w-10 text-white/80" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold text-white">{t('enterprise.services.designCollaboration.title')}</h3>
                        <p className="text-white/80">{t('enterprise.services.designCollaboration.description')}</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <Cpu className="h-10 w-10 text-white/80" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold text-white">{t('enterprise.services.aiBackend.title')}</h3>
                        <p className="text-white/80">{t('enterprise.services.aiBackend.description')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="container px-4 md:px-6 max-w-7xl mx-auto">
          <div className="bg-white dark:bg-gray-950 rounded-lg border border-gray-200 dark:border-gray-800 p-8 md:p-12 shadow-lg">
            <div className="grid gap-6 lg:grid-cols-2 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">{t('cta.title')}</h2>
                <p className="text-xl mb-6 text-gray-600 dark:text-gray-400">{t('cta.subtitle')}</p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg">
                    {t('cta.startTrial')}
                  </Button>
                  <Button size="lg" variant="outline" className="border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 hover:bg-gray-100 dark:hover:bg-gray-900 font-medium rounded-lg">
                    {t('cta.viewTemplates')}
                  </Button>
                </div>
              </div>
              <div className="lg:ml-auto">
                <div className="aspect-video bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="rounded-full bg-white/90 dark:bg-gray-900/90 p-4 shadow-lg">
                      <Play className="h-10 w-10 text-blue-600" />
                    </div>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-white/90 dark:bg-gray-900/90 p-4 rounded-lg shadow-sm">
                      <div className="h-3 w-3/4 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                      <div className="h-3 w-1/2 bg-gray-300 dark:bg-gray-600 rounded"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}